import { t } from "./i18n";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";

type ReviewAppearance = "petrol" | "paper";
const STORAGE_KEY = "ziarcadia:vocabulary-review:appearance:v1";
const CHANGE_EVENT = "ziarcadia:vocabulary-review:appearance";
const read = (fallback: ReviewAppearance): ReviewAppearance => {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return value === "petrol" || value === "paper" ? value : fallback;
    } catch { return fallback; }
};

/** Both reviews share one browser preference; choosing a palette changes paint without rebuilding the list. */
export const createVocabularyReviewAppearance = (root: HTMLElement, fallback: ReviewAppearance) => {
    let appearance = read(fallback);
    const label = () => t(appearance === "paper" ? "vocabularyUseDarkPalette" : "vocabularyUsePaperPalette");
    const refresh = () => {
        root.dataset.vocabularyReviewAppearance = appearance;
        root.querySelectorAll<HTMLButtonElement>("[data-review-palette]").forEach(button => {
            button.setAttribute("aria-label", label());
            button.setAttribute("aria-pressed", String(appearance === "paper"));
            button.title = label();
        });
    };
    const click = (event: Event) => {
        if (!(event.target instanceof Element) || !event.target.closest("[data-review-palette]")) return;
        event.preventDefault();
        appearance = appearance === "paper" ? "petrol" : "paper";
        try { localStorage.setItem(STORAGE_KEY, appearance); } catch { /* The palette still works for this view. */ }
        window.dispatchEvent(new Event(CHANGE_EVENT));
        refresh();
    };
    const sync = () => { appearance = read(appearance); refresh(); };
    const storage = (event: StorageEvent) => { if (event.key === STORAGE_KEY || event.key === null) sync(); };
    root.addEventListener("click", click);
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", storage);
    refresh();
    return {
        refresh,
        renderButton: () => `<button type="button" class="secondary-button review-palette-button" data-review-palette aria-label="${label()}" title="${label()}" aria-pressed="${appearance === "paper"}">${renderZiarcadiaIcon("palette")}</button>`,
        destroy: () => {
            root.removeEventListener("click", click);
            window.removeEventListener(CHANGE_EVENT, sync);
            window.removeEventListener("storage", storage);
            delete root.dataset.vocabularyReviewAppearance;
        }
    };
};
